import { ReactNode, useState } from "react";
import { Link } from "react-router-dom";

import styles from "./Tabs.module.css";

interface TabDef {
  id: string;
  label: string;
  /** Mode A — content-rendering tab. Component renders the panel itself. */
  content?: ReactNode;
  /** Mode B — link tab. Component renders an <a> via react-router Link. */
  to?: string;
}

interface Props {
  tabs: TabDef[];
  /** Mode A only: which tab to render on mount (defaults to first). */
  defaultId?: string;
  /**
   * When provided, the component is controlled. In link mode (tabs have `to`)
   * this is the only way to mark the active tab — there's no internal state
   * and no rendered panel.
   */
  activeId?: string;
  onChange?: (id: string) => void;
}

/**
 * Tabs supports two modes:
 *  - Mode A (content): each tab supplies `content`; the component owns the
 *    active state and renders the selected panel inline.
 *  - Mode B (link): each tab supplies `to`; tabs render as <Link>s. The active
 *    tab is determined by `activeId`. No panel is rendered — the router/page
 *    is responsible for the content below.
 */
export function Tabs({ tabs, defaultId, activeId, onChange }: Props) {
  const linkMode = tabs.some((t) => t.to);
  const [internal, setInternal] = useState(defaultId ?? tabs[0]?.id ?? "");
  const active = linkMode ? (activeId ?? "") : (activeId ?? internal);

  function pick(id: string) {
    if (!linkMode && activeId === undefined) setInternal(id);
    onChange?.(id);
  }

  if (linkMode) {
    return (
      <div className={styles.root}>
        <div role="tablist" className={styles.list}>
          {tabs.map((t) => {
            const isActive = t.id === active;
            const className = `${styles.tab} ${isActive ? styles.active : ""}`;
            return t.to ? (
              <Link
                key={t.id}
                to={t.to}
                role="tab"
                aria-selected={isActive}
                className={className}
                onClick={() => onChange?.(t.id)}
              >
                {t.label}
              </Link>
            ) : (
              // Mixed-mode fallback: a tab without `to` in link mode renders
              // as an inert button. We don't expect this in practice.
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={className}
                onClick={() => pick(t.id)}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const cur = tabs.find((t) => t.id === active);
  return (
    <div className={styles.root}>
      <div role="tablist" className={styles.list}>
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={t.id === active}
            className={`${styles.tab} ${t.id === active ? styles.active : ""}`}
            onClick={() => pick(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" className={styles.panel}>
        {cur?.content}
      </div>
    </div>
  );
}
